import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usuarioService } from '@/services/api';
import { cronogramaService } from '@/services/cronogramasApi';
import { crmBoardsService, crmNegociosService, crmContasService, crmContatosService } from '@/services/crmApi';
import { CrmImportCsvDialog } from '@/components/crm/CrmImportCsvDialog';
import { CrmBoard, CrmNegocio, CrmConta, CrmContato } from '@/types/crm';
import { Usuario } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { CrmSubNav } from '@/components/crm/CrmSubNav';
import { CrmEmpresaSelector } from '@/components/crm/CrmEmpresaSelector';
import { useCrmEmpresaAtiva } from '@/hooks/useCrmEmpresaAtiva';
import { usePollingRefresh } from '@/hooks/usePollingRefresh';
import { CrmBoardView } from '@/components/crm/CrmBoardView';
import { CrmNegocioListView } from '@/components/crm/CrmNegocioListView';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Loader2, Trash2, History, PartyPopper, Search, XCircle, List, LayoutDashboard, X, Settings, ChevronDown, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { MultiSelectCombobox, MultiSelectItem } from '@/components/ui/multi-select-combobox';
import { tagColor } from '@/lib/tagColor';
import { CRM_BOARD_TEMPLATES } from '@/lib/crmBoardTemplates';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

const NONE_VALUE = '__none__';

// Intervalo de polling do Kanban — tunável se sobrecarregar o servidor ou parecer devagar.
const NEGOCIOS_POLL_MS = 8000;

const EMPTY_FORM = {
  titulo: '',
  valor: '',
  conta_id: '',
  contato_id: '',
  responsavel_ids: [] as string[],
  data_previsao_fechamento: '',
  tags: [] as string[],
};

export default function Crm() {
  const { isSuperAdmin, empresas, selectedEmpresaId, setSelectedEmpresaId, empresaId } = useCrmEmpresaAtiva();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') === 'list' ? 'list' : 'board') as 'list' | 'board';
  const [boards, setBoards] = useState<CrmBoard[]>([]);
  const [boardId, setBoardId] = useState<string>('');
  const [isNovoBoardOpen, setIsNovoBoardOpen] = useState(false);
  const [novoBoardNome, setNovoBoardNome] = useState('');
  const [novoBoardTemplateId, setNovoBoardTemplateId] = useState(CRM_BOARD_TEMPLATES[0].id);
  const [isEditBoardOpen, setIsEditBoardOpen] = useState(false);
  const [editBoardForm, setEditBoardForm] = useState({ nome: '', is_default: false });
  const [deleteBoardId, setDeleteBoardId] = useState<string | null>(null);
  const [negocios, setNegocios] = useState<CrmNegocio[]>([]);
  const [contas, setContas] = useState<CrmConta[]>([]);
  const [contatos, setContatos] = useState<CrmContato[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNegocio, setEditingNegocio] = useState<CrmNegocio | null>(null);
  const [novaColunaId, setNovaColunaId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tagInput, setTagInput] = useState('');

  // Prompt pós-"Ganho" (etapa 6): oferece iniciar a execução assim que o negócio fecha.
  const [ganhoNegocioId, setGanhoNegocioId] = useState<string | null>(null);

  // Moves otimistas ainda em voo (negocioId -> column_id) — evita que um poll que chega no
  // meio de um PATCH /mover ainda não confirmado sobrescreva o card de volta pra coluna
  // antiga (loadNegocios preserva esses valores até o PATCH resolver).
  const pendingMovesRef = useRef<Record<string, string>>({});

  // Filtros do Kanban — só camada de apresentação sobre `negocios`; não afeta o estado base
  // usado por handleMove/pendingMovesRef.
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroResponsavelId, setFiltroResponsavelId] = useState('all');
  const [filtroContaId, setFiltroContaId] = useState('all');
  const [filtroTag, setFiltroTag] = useState('all');
  const hasActiveFilters =
    !!filtroTexto || filtroResponsavelId !== 'all' || filtroContaId !== 'all' || filtroTag !== 'all';
  const clearFiltros = () => {
    setFiltroTexto('');
    setFiltroResponsavelId('all');
    setFiltroContaId('all');
    setFiltroTag('all');
  };

  const board = boards.find((b) => b.id === boardId) || null;

  const tagsDisponiveis = useMemo(
    () => Array.from(new Set(negocios.flatMap((n) => n.tags || []))).sort(),
    [negocios],
  );

  const negociosFiltrados = useMemo(() => {
    const texto = filtroTexto.trim().toLowerCase();
    return negocios.filter((n) => {
      if (texto && !n.titulo.toLowerCase().includes(texto)) return false;
      if (filtroResponsavelId !== 'all' && !n.responsaveis?.some((r) => r.id === filtroResponsavelId)) return false;
      if (filtroContaId !== 'all' && n.conta_id !== filtroContaId) return false;
      if (filtroTag !== 'all' && !(n.tags || []).includes(filtroTag)) return false;
      return true;
    });
  }, [negocios, filtroTexto, filtroResponsavelId, filtroContaId, filtroTag]);

  const loadAuxData = async () => {
    if (isSuperAdmin && !empresaId) {
      setContas([]);
      setContatos([]);
      setUsuarios([]);
      return;
    }
    try {
      const [contasRes, contatosRes] = await Promise.all([
        crmContasService.getAll(isSuperAdmin ? empresaId : undefined),
        crmContatosService.getAll(undefined, isSuperAdmin ? empresaId : undefined),
      ]);
      if (contasRes.success) setContas(contasRes.data);
      if (contatosRes.success) setContatos(contatosRes.data);
      if (empresaId) {
        const usuariosRes = await usuarioService.getByEmpresa(empresaId);
        if (usuariosRes.success) setUsuarios(usuariosRes.data);
      }
    } catch (error) {
      console.error('Error loading aux data:', error);
    }
  };

  // `preferredId`: mantém um board específico selecionado após o reload (ex: editar o nome do
  // board não-padrão não deve pular a seleção de volta pro padrão) — sem isso, cairia sempre
  // no padrão/primeiro, que só é o comportamento certo ao excluir o board selecionado.
  const loadBoards = async (preferredId?: string) => {
    if (isSuperAdmin && !empresaId) {
      setBoards([]);
      setBoardId('');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const result = await crmBoardsService.getAll(isSuperAdmin ? empresaId : undefined);
      if (result.success) {
        setBoards(result.data);
        const preferido = preferredId ? result.data.find((b) => b.id === preferredId) : undefined;
        const alvo = preferido || result.data.find((b) => b.is_default) || result.data[0];
        setBoardId(alvo ? alvo.id : '');
      }
    } catch (error) {
      console.error('Error loading boards:', error);
      toast.error('Erro ao carregar quadros');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBoard = async () => {
    if (isSuperAdmin && !empresaId) {
      toast.error('Selecione uma empresa antes de criar um quadro');
      return;
    }
    try {
      const template = CRM_BOARD_TEMPLATES.find((t) => t.id === novoBoardTemplateId) || CRM_BOARD_TEMPLATES[0];
      const result = await crmBoardsService.create({
        nome: novoBoardNome,
        empresa_id: isSuperAdmin ? empresaId : undefined,
        colunas: template.colunas,
      });
      if (result.success) {
        toast.success('Quadro criado');
        setIsNovoBoardOpen(false);
        setNovoBoardNome('');
        setNovoBoardTemplateId(CRM_BOARD_TEMPLATES[0].id);
        await loadBoards(result.data.id);
      } else {
        toast.error(result.error || 'Erro ao criar quadro');
      }
    } catch {
      toast.error('Erro ao criar quadro');
    }
  };

  const openEditBoard = () => {
    if (!board) return;
    setEditBoardForm({ nome: board.nome, is_default: board.is_default });
    setIsEditBoardOpen(true);
  };

  const handleUpdateBoard = async () => {
    if (!board) return;
    try {
      const result = await crmBoardsService.update(board.id, editBoardForm);
      if (result.success) {
        toast.success('Quadro atualizado');
        setIsEditBoardOpen(false);
        await loadBoards(board.id);
      } else {
        toast.error(result.error || 'Erro ao atualizar quadro');
      }
    } catch {
      toast.error('Erro ao atualizar quadro');
    }
  };

  const handleDeleteBoard = async () => {
    if (!deleteBoardId) return;
    try {
      const result = await crmBoardsService.delete(deleteBoardId);
      if (result.success) {
        toast.success('Quadro excluído');
        setIsEditBoardOpen(false);
        await loadBoards();
      } else {
        toast.error(result.error || 'Erro ao excluir quadro');
      }
    } catch {
      toast.error('Erro ao excluir quadro');
    }
    setDeleteBoardId(null);
  };

  const loadNegocios = async (id: string) => {
    try {
      const result = await crmNegociosService.getAll(id);
      if (result.success) {
        const pending = pendingMovesRef.current;
        const negocios = Object.keys(pending).length === 0
          ? result.data
          : result.data.map((n) => (pending[n.id] ? { ...n, column_id: pending[n.id] } : n));
        setNegocios(negocios);
      }
    } catch (error) {
      console.error('Error loading negocios:', error);
      toast.error('Erro ao carregar negócios');
    }
  };

  useEffect(() => {
    loadBoards();
    loadAuxData();
  }, [empresaId]);

  useEffect(() => {
    if (boardId) loadNegocios(boardId);
    else setNegocios([]);
  }, [boardId]);

  usePollingRefresh(() => loadNegocios(boardId), NEGOCIOS_POLL_MS, !!boardId);

  const handleMove = async (negocioId: string, columnId: string) => {
    const negocioAntes = negocios.find((n) => n.id === negocioId);
    // Otimista: aplica local antes da resposta, pra o drag parecer instantâneo.
    pendingMovesRef.current[negocioId] = columnId;
    setNegocios((prev) => prev.map((n) => (n.id === negocioId ? { ...n, column_id: columnId } : n)));
    try {
      const result = await crmNegociosService.mover(negocioId, columnId);
      // Só limpa a "trava" se ela ainda representa ESTA chamada — um segundo drag do mesmo
      // card, disparado antes desta resposta chegar, já sobrescreveu o ref com o destino mais
      // recente, e apagar incondicionalmente deixaria esse move mais novo sem proteção contra
      // o próximo poll (que aplicaria de volta o destino antigo, já superado).
      if (pendingMovesRef.current[negocioId] === columnId) {
        delete pendingMovesRef.current[negocioId];
      }
      if (!result.success) {
        toast.error(result.error || 'Erro ao mover negócio');
        loadNegocios(boardId);
        return;
      }

      // Só oferece o prompt na transição PARA uma coluna Ganho (não a cada reload da tela) e
      // só se o negócio ainda não tiver um Cronograma vinculado — combinado com o usuário
      // pra "perguntar", não criar automaticamente.
      const colunaDestino = board?.colunas?.find((c) => c.id === columnId);
      if (colunaDestino?.is_ganho && !negocioAntes?.cronograma_gerado_id) {
        setGanhoNegocioId(negocioId);
      }
    } catch (error) {
      if (pendingMovesRef.current[negocioId] === columnId) {
        delete pendingMovesRef.current[negocioId];
      }
      toast.error('Erro ao mover negócio');
      loadNegocios(boardId);
    }
  };

  const handleCriarCronogramaPosGanho = async () => {
    if (!ganhoNegocioId || !empresaId) return;
    const negocio = negocios.find((n) => n.id === ganhoNegocioId);
    if (!negocio) return;
    try {
      const criado = await cronogramaService.create({ titulo: negocio.titulo, area: 'Comercial', empresa_id: empresaId });
      if (!criado.success || !criado.data) {
        toast.error(criado.error || 'Erro ao criar projeto');
        return;
      }
      const vinculo = await crmNegociosService.vincularCronograma(ganhoNegocioId, criado.data.id);
      if (vinculo.success) {
        toast.success('Projeto criado e vinculado');
        loadNegocios(boardId);
      } else {
        toast.error(vinculo.error || 'Projeto criado, mas não foi possível vincular');
      }
    } catch (error) {
      toast.error('Erro ao criar projeto');
    } finally {
      setGanhoNegocioId(null);
    }
  };

  const openNewNegocio = (columnId: string) => {
    setEditingNegocio(null);
    setNovaColunaId(columnId);
    setForm(EMPTY_FORM);
    setIsDialogOpen(true);
  };

  const openEditNegocio = (negocio: CrmNegocio) => {
    setEditingNegocio(negocio);
    setNovaColunaId(null);
    setForm({
      titulo: negocio.titulo,
      valor: negocio.valor?.toString() || '',
      conta_id: negocio.conta_id || '',
      contato_id: negocio.contato_id || '',
      responsavel_ids: (negocio.responsaveis || []).map((r) => r.id),
      data_previsao_fechamento: negocio.data_previsao_fechamento || '',
      tags: negocio.tags || [],
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingNegocio(null);
    setNovaColunaId(null);
    setForm(EMPTY_FORM);
    setTagInput('');
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (!tag || form.tags.includes(tag) || form.tags.length >= 10) return;
    setForm((f) => ({ ...f, tags: [...f.tags, tag] }));
    setTagInput('');
  };

  const handleRemoveTag = (tag: string) => {
    setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));
  };

  const handleSave = async () => {
    if (!editingNegocio && isSuperAdmin && !empresaId) {
      toast.error('Selecione uma empresa antes de criar um negócio');
      return;
    }
    const payload = {
      titulo: form.titulo,
      valor: form.valor ? Number(form.valor) : null,
      conta_id: form.conta_id || null,
      contato_id: form.contato_id || null,
      responsavel_ids: form.responsavel_ids.length > 0 ? form.responsavel_ids : undefined,
      data_previsao_fechamento: form.data_previsao_fechamento || null,
      tags: form.tags,
    };

    try {
      const result = editingNegocio
        ? await crmNegociosService.update(editingNegocio.id, payload)
        : await crmNegociosService.create({
            ...payload,
            board_id: boardId,
            column_id: novaColunaId || undefined,
            empresa_id: isSuperAdmin ? empresaId : undefined,
          });

      if (result.success) {
        toast.success(editingNegocio ? 'Negócio atualizado' : 'Negócio criado');
        loadNegocios(boardId);
        closeDialog();
      } else {
        toast.error(result.error || 'Erro ao salvar negócio');
      }
    } catch (error) {
      toast.error('Erro ao salvar negócio');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const result = await crmNegociosService.delete(deleteId);
      if (result.success) {
        toast.success('Negócio excluído');
        setIsDialogOpen(false);
        loadNegocios(boardId);
      } else {
        toast.error(result.error || 'Erro ao excluir negócio');
      }
    } catch (error) {
      toast.error('Erro ao excluir negócio');
    }
    setDeleteId(null);
  };

  const contatosDaConta = useCallback(
    (contaId: string) => contatos.filter((c) => c.conta_id === contaId),
    [contatos],
  );

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>

        <GuiaModuloFlutuante imagemSrc="/guias-modulos/crm.png" titulo="CRM" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="animate-fade-in space-y-6">
        <CrmSubNav
          active="negocios"
          right={
            isSuperAdmin ? (
              <CrmEmpresaSelector empresas={empresas} value={selectedEmpresaId} onChange={setSelectedEmpresaId} />
            ) : undefined
          }
        />
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Negócios</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-2xl font-bold tracking-tight text-foreground transition-colors hover:text-primary">
                  {board?.nome || 'Nenhum quadro'}
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {boards.map((b) => (
                  <DropdownMenuItem key={b.id} onClick={() => setBoardId(b.id)}>
                    <Check className={cn('mr-2 h-4 w-4', b.id === boardId ? 'opacity-100' : 'opacity-0')} />
                    {b.nome}
                  </DropdownMenuItem>
                ))}
                {boards.length > 0 && <DropdownMenuSeparator />}
                {board && (
                  <DropdownMenuItem onClick={openEditBoard}>
                    <Settings className="mr-2 h-4 w-4" /> Editar quadro
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setIsNovoBoardOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Novo quadro
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-2">
            <CrmImportCsvDialog
              titulo="Importar Negócios via CSV"
              templateNomeArquivo="modelo-negocios.csv"
              templateColunas={['titulo', 'valor', 'conta', 'contato', 'responsavel', 'etapa', 'data_previsao_fechamento']}
              // Coluna "responsavel" aceita nome ou e-mail; múltiplos responsáveis separados por ";".
              templateExemplo={['Consultoria de implantação', '18000', 'Acme Distribuidora Ltda', '', 'Ana Souza;Bruno Lima', 'Lead', '31/12/2026']}
              onImport={(file) => crmNegociosService.importCsv(file, { empresaId: isSuperAdmin ? empresaId : undefined, boardId })}
              onSuccess={() => loadNegocios(boardId)}
              disabled={!boardId}
              disabledMotivo="Selecione um quadro primeiro"
            />
            <div className="flex items-center gap-0.5 rounded-lg border p-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={view === 'board' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setSearchParams({ view: 'board' })}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Visualização em quadro</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={view === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setSearchParams({ view: 'list' })}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Visualização em lista</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {isSuperAdmin && !empresaId ? (
          <p className="text-muted-foreground">Selecione uma empresa acima para ver o pipeline de Negócios.</p>
        ) : !board ? (
          <p className="text-muted-foreground">
            Nenhum quadro cadastrado ainda — clique em <Plus className="inline h-3.5 w-3.5" /> acima para criar o primeiro.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="relative min-w-[200px] flex-1">
                <Label className="text-xs text-muted-foreground">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por título..."
                    value={filtroTexto}
                    onChange={(e) => setFiltroTexto(e.target.value)}
                    className="h-9 pl-9"
                  />
                </div>
              </div>
              <div className="w-full sm:w-auto sm:min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Responsável</Label>
                <Select value={filtroResponsavelId} onValueChange={setFiltroResponsavelId}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {usuarios.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-auto sm:min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Conta</Label>
                <Select value={filtroContaId} onValueChange={setFiltroContaId}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {contas.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {tagsDisponiveis.length > 0 && (
                <div className="w-full sm:w-auto sm:min-w-[140px]">
                  <Label className="text-xs text-muted-foreground">Tag</Label>
                  <Select value={filtroTag} onValueChange={setFiltroTag}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {tagsDisponiveis.map((tag) => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFiltros} className="h-9 w-full sm:w-auto">
                  <XCircle className="mr-1 h-4 w-4" />
                  Limpar
                </Button>
              )}
            </div>

            {view === 'list' ? (
              <CrmNegocioListView board={board} negocios={negociosFiltrados} onRowClick={openEditNegocio} />
            ) : (
              <CrmBoardView
                board={board}
                negocios={negociosFiltrados}
                onMove={handleMove}
                onCardClick={openEditNegocio}
                onAddCard={openNewNegocio}
                onColunasChange={() => loadBoards(boardId)}
              />
            )}
          </>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingNegocio ? 'Editar Negócio' : 'Novo Negócio'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="neg_titulo">Título *</Label>
              <Input
                id="neg_titulo"
                value={form.titulo}
                onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                placeholder="Ex: Licença anual — Acme"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="neg_valor">Valor (R$)</Label>
                <Input
                  id="neg_valor"
                  type="number"
                  value={form.valor}
                  onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="neg_previsao">Previsão de fechamento</Label>
                <Input
                  id="neg_previsao"
                  type="date"
                  value={form.data_previsao_fechamento}
                  onChange={(e) => setForm((f) => ({ ...f, data_previsao_fechamento: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="neg_conta">Conta</Label>
                <Select
                  value={form.conta_id || NONE_VALUE}
                  onValueChange={(v) => setForm((f) => ({ ...f, conta_id: v === NONE_VALUE ? '' : v, contato_id: '' }))}
                >
                  <SelectTrigger id="neg_conta">
                    <SelectValue placeholder="Sem conta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>Sem conta</SelectItem>
                    {contas.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="neg_contato">Contato</Label>
                <Select
                  value={form.contato_id || NONE_VALUE}
                  onValueChange={(v) => setForm((f) => ({ ...f, contato_id: v === NONE_VALUE ? '' : v }))}
                >
                  <SelectTrigger id="neg_contato">
                    <SelectValue placeholder="Sem contato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>Sem contato</SelectItem>
                    {(form.conta_id ? contatosDaConta(form.conta_id) : contatos).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="neg_responsavel">Responsáveis</Label>
              <MultiSelectCombobox
                items={usuarios.map((u): MultiSelectItem => ({ id: u.id, nome: u.nome, avatarUrl: u.avatar_url }))}
                value={usuarios
                  .filter((u) => form.responsavel_ids.includes(u.id))
                  .map((u): MultiSelectItem => ({ id: u.id, nome: u.nome, avatarUrl: u.avatar_url }))}
                onChange={(selecionados) => setForm((f) => ({ ...f, responsavel_ids: selecionados.map((s) => s.id) }))}
                allowCreate={false}
                placeholder="Eu mesmo (sem seleção)"
                emptyLabel="Nenhum usuário encontrado."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="neg_tags">Tags</Label>
              <div className="flex gap-2">
                <Input
                  id="neg_tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Digite e pressione Enter"
                />
                <Button type="button" variant="outline" onClick={handleAddTag} disabled={!tagInput.trim()}>
                  Adicionar
                </Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {form.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="gap-1 pr-1"
                      style={{ borderColor: tagColor(tag), color: tagColor(tag) }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="rounded-full hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between border-t pt-4 sm:justify-between">
            {editingNegocio ? (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  title="Excluir negócio"
                  onClick={() => setDeleteId(editingNegocio.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Ver linha do tempo"
                  onClick={() => navigate(`/crm/negocios/${editingNegocio.id}`)}
                >
                  <History className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeDialog}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={!form.titulo}>
                {editingNegocio ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este negócio? O histórico de movimentação também será perdido. Esta ação
              não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!ganhoNegocioId} onOpenChange={(open) => !open && setGanhoNegocioId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PartyPopper className="h-5 w-5 text-primary" />
              Negócio ganho!
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Quer já iniciar a execução criando um Projeto vinculado a este negócio? Você também pode vincular a um
            projeto existente depois, pela tela de detalhe do negócio.
          </p>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button onClick={handleCriarCronogramaPosGanho} className="w-full">
              Criar Projeto
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                if (ganhoNegocioId) navigate(`/crm/negocios/${ganhoNegocioId}`);
                setGanhoNegocioId(null);
              }}
            >
              Ver detalhes do negócio
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setGanhoNegocioId(null)}>
              Agora não
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isNovoBoardOpen} onOpenChange={(open) => !open && setIsNovoBoardOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Quadro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="board_nome">Nome</Label>
              <Input
                id="board_nome"
                value={novoBoardNome}
                onChange={(e) => setNovoBoardNome(e.target.value)}
                placeholder="Ex: Funil Comercial"
              />
            </div>
            <div className="space-y-2">
              <Label>Modelo</Label>
              <RadioGroup value={novoBoardTemplateId} onValueChange={setNovoBoardTemplateId} className="gap-2">
                {CRM_BOARD_TEMPLATES.map((template) => (
                  <label
                    key={template.id}
                    htmlFor={`template_${template.id}`}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-muted/50 has-[:checked]:border-primary"
                  >
                    <RadioGroupItem value={template.id} id={`template_${template.id}`} className="mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{template.nome}</p>
                      <p className="text-xs text-muted-foreground">{template.descricao}</p>
                      {template.colunas.length > 0 && (
                        <div className="flex gap-1 pt-0.5">
                          {template.colunas.map((col) => (
                            <span key={col.nome} className="h-2 w-2 rounded-full" style={{ backgroundColor: col.cor }} />
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNovoBoardOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateBoard} disabled={!novoBoardNome.trim()}>
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditBoardOpen} onOpenChange={(open) => !open && setIsEditBoardOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Quadro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_board_nome">Nome</Label>
              <Input
                id="edit_board_nome"
                value={editBoardForm.nome}
                onChange={(e) => setEditBoardForm((f) => ({ ...f, nome: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit_board_default"
                checked={editBoardForm.is_default}
                disabled={editBoardForm.is_default}
                onCheckedChange={(v) => setEditBoardForm((f) => ({ ...f, is_default: !!v }))}
              />
              <Label htmlFor="edit_board_default" className="font-normal">Quadro padrão</Label>
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => board && setDeleteBoardId(board.id)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir quadro
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditBoardOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdateBoard} disabled={!editBoardForm.nome.trim()}>
                Salvar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteBoardId} onOpenChange={() => setDeleteBoardId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão do quadro</AlertDialogTitle>
            <AlertDialogDescription>
              Todas as colunas e Negócios deste quadro serão excluídos junto. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBoard} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/crm.png" titulo="CRM" />
    </AppLayout>
  );
}
